import { assistantOptions } from './assistant.config';
import { Injectable, InjectionToken, OnDestroy, inject, signal } from '@angular/core';

/** Browser speech API is not yet included consistently in TypeScript DOM typings. */
export interface BrowserRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult:
    | ((event: {
        resultIndex: number;
        results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>;
      }) => void)
    | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export const ASSISTANT_VOICE_CONFIG = new InjectionToken('Assistant voice configuration', {
  providedIn: 'root',
  factory: () => ({ enabled: assistantOptions.voiceEnabled, language: assistantOptions.voiceLanguage, maxListeningMs: assistantOptions.voiceMaxListeningMs }),
});

export const BROWSER_RECOGNITION = new InjectionToken<(() => BrowserRecognition) | null>(
  'Browser recognition',
  {
    providedIn: 'root',
    factory: () => {
      if (typeof window === 'undefined' || !window.isSecureContext) return null;
      const browser = window as unknown as {
        SpeechRecognition?: new () => BrowserRecognition;
        webkitSpeechRecognition?: new () => BrowserRecognition;
      };
      const Recognition = browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
      return Recognition ? () => new Recognition() : null;
    },
  },
);

export interface VoicePlayback {
  speak(text: string, done: () => void, failed: () => void): void;
  cancel(): void;
}

export const BROWSER_PLAYBACK = new InjectionToken<VoicePlayback | null>('Browser playback', {
  providedIn: 'root',
  factory: () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    return {
      speak: (text, done, failed) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = assistantOptions.voiceLanguage;
        utterance.onend = done;
        utterance.onerror = failed;
        window.speechSynthesis.speak(utterance);
      },
      cancel: () => window.speechSynthesis.cancel(),
    };
  },
});

export const VOICE_CONVERSATION_CONFIG = new InjectionToken('Voice conversation timing', {
  providedIn: 'root',
  factory: () => ({ silenceMs: 1300, maxPlaybackMs: 180000 }),
});

/** Explicitly started, turn-based voice conversation; audio is never stored by this app. */
@Injectable({ providedIn: 'root' })
export class AssistantVoiceService implements OnDestroy {
  private readonly config = inject(ASSISTANT_VOICE_CONFIG);
  private readonly createRecognition = inject(BROWSER_RECOGNITION);
  private readonly playback = inject(BROWSER_PLAYBACK);
  private readonly timing = inject(VOICE_CONVERSATION_CONFIG);
  readonly enabled = this.config.enabled;
  readonly supported = this.enabled && !!this.createRecognition;
  readonly conversationSupported = this.supported && !!this.playback;
  readonly active = signal(false);
  readonly phase = signal<'off' | 'listening' | 'thinking' | 'speaking'>('off');
  private silenceTimer?: ReturnType<typeof setTimeout>;
  private playbackTimer?: ReturnType<typeof setTimeout>;
  private generation = 0;
  private submit?: (text: string) => boolean;
  private utterance = '';
  private maxChars = 2000;
  readonly listening = signal(false);
  readonly interim = signal('');
  readonly notice = signal('');
  private recognition: BrowserRecognition | null = null;
  private timeout?: ReturnType<typeof setTimeout>;

  start(onText: (text: string) => void): void {
    if (!this.supported || this.recognition) return;
    this.notice.set('');
    this.interim.set('');
    try {
      const recognition = this.createRecognition!();
      this.recognition = recognition;
      recognition.lang = this.config.language;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.onresult = (event) => {
        if (this.recognition !== recognition) return;
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) onText(result[0].transcript.trim());
          else interim += result[0].transcript;
          if (this.recognition !== recognition) return;
        }
        this.interim.set(interim.slice(0, 2000));
        if (this.active()) {
          clearTimeout(this.silenceTimer);
          // Never auto-submit an unfinished interim recognition result.
          if (!interim && this.utterance.trim()) {
            this.silenceTimer = setTimeout(() => this.submitUtterance(), this.timing.silenceMs);
          }
        }
      };
      recognition.onerror = ({ error }) => {
        if (this.recognition !== recognition) return;
        this.cancel();
        this.notice.set(
          error === 'not-allowed' || error === 'service-not-allowed'
            ? 'Microphone access was denied. Check your browser permissions or type your question.'
            : error === 'no-speech'
              ? 'No speech was detected. Try again or type your question.'
              : error === 'audio-capture'
                ? 'No microphone is available. Check your device or type your question.'
                : 'Voice input is unavailable. Please try again or type your question.',
        );
      };
      recognition.onend = () => {
        if (this.recognition !== recognition) return;
        this.release();
        if (this.active()) {
          this.cancel();
          this.notice.set('Voice conversation stopped. Select Start to try again.');
        }
      };
      this.listening.set(true);
      recognition.start();
      this.timeout = setTimeout(() => this.cancel(), this.config.maxListeningMs);
    } catch {
      this.cancel();
      this.notice.set('Voice input could not start. Please type your question.');
    }
  }

  /** Stop gracefully to allow the browser to deliver its final transcript. */
  stop(): void {
    try {
      this.recognition?.stop();
    } catch {
      this.cancel();
    }
  }

  /** Invalidate callbacks before aborting so a previous session cannot refill a draft. */
  cancel(): void {
    this.active.set(false);
    this.phase.set('off');
    this.generation++;
    clearTimeout(this.silenceTimer);
    clearTimeout(this.playbackTimer);
    this.playback?.cancel();
    this.submit = undefined;
    this.utterance = '';
    const recognition = this.recognition;
    this.release();
    this.notice.set('');
    try {
      recognition?.abort();
    } catch {
      /* The browser may already have ended capture. */
    }
  }

  startConversation(submit: (text: string) => boolean, maxChars: number): void {
    if (!this.conversationSupported || this.active()) return;
    this.submit = submit;
    this.maxChars = maxChars;
    this.active.set(true);
    this.listenForTurn();
  }

  private listenForTurn(): void {
    if (!this.active()) return;
    this.utterance = '';
    this.phase.set('listening');
    this.start((text) => {
      this.utterance = `${this.utterance} ${text}`.trim();
      if (this.utterance.length > this.maxChars) {
        this.cancel();
        this.notice.set('That question was too long. Please try a shorter question.');
      }
    });
  }

  private submitUtterance(): void {
    if (!this.active() || !this.listening()) return;
    const text = this.utterance.trim();
    const recognition = this.recognition;
    this.release(); // Detach recognition before sending, preventing echo and late transcripts.
    try {
      recognition?.abort();
    } catch {
      /* Already stopped. */
    }
    this.phase.set('thinking');
    if (!text || !this.submit?.(text)) this.cancel();
  }

  /** Speak only the final validated answer; partial output can still be replaced by a refusal. */
  answerCompleted(text: string): void {
    if (!this.active()) return;
    const generation = this.generation;
    this.phase.set('speaking');
    const plain = text
      .replace(/\[S\d+\]/g, '')
      .replace(/[*#`]/g, '')
      .trim();
    const failed = () => {
      if (generation !== this.generation) return;
      this.cancel();
      this.notice.set(
        'Voice playback stopped. You can read the answer or restart voice conversation.',
      );
    };
    if (!plain) {
      failed();
      return;
    }
    this.playbackTimer = setTimeout(failed, this.timing.maxPlaybackMs);
    try {
      this.playback!.speak(
        plain,
        () => {
          if (generation !== this.generation || !this.active()) return;
          clearTimeout(this.playbackTimer);
          this.listenForTurn();
        },
        failed,
      );
    } catch {
      failed();
    }
  }

  private release(): void {
    clearTimeout(this.timeout);
    if (this.recognition) {
      this.recognition.onresult = null;
      this.recognition.onerror = null;
      this.recognition.onend = null;
    }
    this.recognition = null;
    this.listening.set(false);
    this.interim.set('');
  }

  ngOnDestroy(): void {
    this.cancel();
  }
}
